import React, { useRef } from 'react';
import { Box, Typography, Chip, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import Swal from 'sweetalert2';
import {
  getDisallowedFilenameCharsMessage,
  hasDisallowedFilenameChars,
} from '../../../common/functions/file_name_validation.function';

const MultipleFileUploadSection = ({
  description,
  downloadLinkText,
  files,
  onFilesChange,
  onFileRemove,
  allowedExtensions = ['.pdf', '.xlsx', '.xls', '.csv', '.msg', '.kmz'],
  allowedMimeTypes = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
    'text/csv',
    'application/vnd.ms-outlook',
    'message/rfc822',
    'application/vnd.google-earth.kmz',
    'application/vnd.google-earth.kmz+xml',
  ],
  accept,
  allowedTypesLabel = 'PDF, Excel, CSV, MSG, or KMZ',
  disabled = false,
}) => {
  const fileInputRef = useRef(null);

  // Validate file type - PDF, Excel, CSV, and MSG files
  const isValidFileType = (file) => {
    const fileName = file.name.toLowerCase();
    return (
      allowedExtensions.some((ext) => fileName.endsWith(ext)) ||
      allowedMimeTypes.includes(file.type)
    );
  };

  const handleFileClick = () => {
    if (disabled) {
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length > 0) {
      // Filter valid files
      const validFiles = selectedFiles.filter((file) => {
        if (hasDisallowedFilenameChars(file.name)) {
          Swal.fire({
            title: 'Invalid file name',
            text: `"${file.name}" has unsupported characters. ${getDisallowedFilenameCharsMessage()}`,
            icon: 'error',
            confirmButtonColor: '#BD1D23',
            customClass: { popup: 'small-swal' },
          });
          return false;
        }
        if (isValidFileType(file)) {
          return true;
        } else {
          Swal.fire({
            title: 'Invalid file type',
            text: `"${file.name}" is not a supported file. Please upload only ${allowedTypesLabel} files.`,
            icon: 'error',
            confirmButtonColor: '#BD1D23',
            customClass: { popup: 'small-swal' },
          });
          return false;
        }
      });

      if (validFiles.length > 0) {
        // Add new files to existing files array
        const newFiles = [...(files || []), ...validFiles];
        onFilesChange(newFiles);
      }
    }
    // Reset input to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) {
      return;
    }
    const droppedFiles = Array.from(e.dataTransfer.files || []);
    if (droppedFiles.length > 0) {
      // Filter valid files
      const validFiles = droppedFiles.filter((file) => {
        if (hasDisallowedFilenameChars(file.name)) {
          Swal.fire({
            title: 'Invalid file name',
            text: `"${file.name}" has unsupported characters. ${getDisallowedFilenameCharsMessage()}`,
            icon: 'error',
            confirmButtonColor: '#BD1D23',
            customClass: { popup: 'small-swal' },
          });
          return false;
        }
        if (isValidFileType(file)) {
          return true;
        } else {
          Swal.fire({
            title: 'Invalid file type',
            text: `"${file.name}" is not a supported file. Please upload only ${allowedTypesLabel} files.`,
            icon: 'error',
            confirmButtonColor: '#BD1D23',
            customClass: { popup: 'small-swal' },
          });
          return false;
        }
      });

      if (validFiles.length > 0) {
        // Add new files to existing files array
        const newFiles = [...(files || []), ...validFiles];
        onFilesChange(newFiles);
      }
    }
  };

  const handleRemoveFile = (index) => {
    if (disabled) {
      return;
    }
    const updatedFiles = files.filter((_, i) => i !== index);
    onFilesChange(updatedFiles);
    if (onFileRemove) {
      onFileRemove(index);
    }
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Box
        onClick={handleFileClick}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        sx={{
          border: '2px dashed #667687ff',
          borderRadius: '4px',
          padding: '40px',
          textAlign: 'center',
          cursor: disabled ? 'default' : 'pointer',
          opacity: disabled ? 0.65 : 1,
          backgroundColor: files && files.length > 0 ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
          transition: 'background-color 0.3s',
          '&:hover': {
            backgroundColor: 'rgba(25, 118, 210, 0.08)',
          },
        }}
      >
        <AddIcon
          sx={{
            fontSize: '38px',
            color: '#BD1D23',
            mb: 1,
            padding: '5px',
            background: '#EBB9BB',
            borderRadius: '10px',
          }}
        />
        <Typography
          sx={{
            fontSize: '16px',
            fontFamily: 'Inter',
            fontWeight: 500,
            color: 'hsla(0, 0%, 0%, 0.85)',
            mb: 1,
          }}
        >
          {description}
        </Typography>
        <Typography
          sx={{
            fontSize: '14px',
            fontFamily: 'Inter',
            color: '#666',
          }}
        >
          {downloadLinkText}{' '}
        </Typography>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={
            accept ||
            '.pdf,.xlsx,.xls,.csv,.msg,.kmz,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv,application/vnd.ms-outlook,message/rfc822,application/vnd.google-earth.kmz,application/vnd.google-earth.kmz+xml'
          }
          disabled={disabled}
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
      </Box>

      {/* Display uploaded files */}
      {files && files.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography
            sx={{
              fontSize: '14px',
              fontFamily: 'Inter',
              fontWeight: 600,
              mb: 1,
            }}
          >
            Uploaded Files ({files.length}):
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {files.map((file, index) => (
              <Chip
                key={index}
                label={file.name}
                onDelete={() => handleRemoveFile(index)}
                disabled={disabled}
                deleteIcon={<DeleteIcon />}
                sx={{
                  backgroundColor: '#f5f5f5',
                  '& .MuiChip-deleteIcon': {
                    color: '#d32f2f',
                    '&:hover': {
                      color: '#b71c1c',
                    },
                  },
                }}
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default MultipleFileUploadSection;
